/**
 * Preview panel composable — follows the same provide/inject pattern as
 * `usePageHeader`. The shell (`BackendTemplate`) calls `providePreviewPanel()`
 * to own the state and render the panel as a true third column. Feature pages
 * call `usePreviewPanel()` to open/close the panel and teleport their preview
 * content into the shell-owned container.
 */
import { inject, provide, reactive, type InjectionKey } from 'vue'

interface PreviewPanelState {
  /** Whether the preview column is currently visible. */
  active: boolean
  /** CSS width value for the panel (default: '420px'). */
  width: string
}

export interface PreviewPanelApi {
  state: PreviewPanelState
  open: (width?: string) => void
  close: () => void
}

const PREVIEW_PANEL_KEY: InjectionKey<PreviewPanelApi> = Symbol('previewPanel')

export function providePreviewPanel(): PreviewPanelApi {
  const state = reactive<PreviewPanelState>({
    active: false,
    width: '420px'
  })

  const api: PreviewPanelApi = {
    state,
    open(width = '420px') {
      state.active = true
      state.width = width
    },
    close() {
      state.active = false
    }
  }

  provide(PREVIEW_PANEL_KEY, api)
  return api
}

export function usePreviewPanel(): PreviewPanelApi | null {
  return inject(PREVIEW_PANEL_KEY, null)
}
